"use client";
import React, {
    useState,
    useCallback,
    useEffect,
    useMemo,
    useRef,
} from "react";
import { FmIcon } from "@/ui-component";
import classnames from "classnames";
import stl from "./styles/notice.module.scss";
import { getSession, setSession } from "@/lib/commonService";
import { pageType } from "@/config/constConf";
import { IAnnouncementProps } from ".";
import { headerStore } from "@/store/pageModules";
import { usePageLayoutContext } from "@/src/provider";
import { usePathname } from "next/navigation";

// 常量定义集中放置，使用更具描述性的常量名
const SESSION_KEY_NOTICE_CLOSED = "NOT_SHOW_SESSION_KEY";
const SESSION_VALUE_NOTICE_CLOSED = "1";

/**
 * 通知横幅显示控制 hook
 */
const useAnnouncementVisibility = (props: IAnnouncementProps) => {
    const pathname = usePathname();
    const { topNotice, page_name } = props;
    const [isVisible, setIsVisible] = useState(true);
    const [showAfterInitialRender, setShowAfterInitialRender] = useState(false);
    const config = usePageLayoutContext((state) => state.config);
    // 检查是否应该显示通知横幅
    const checkVisibility = useCallback(() => {
        // 从配置或props中获取显示和隐藏页面配置
        const showPages = config?.topNotice
            ? config.topNotice.show_pages
            : topNotice.pages;
        const hidePages = config?.topNotice
            ? config.topNotice.hide_pages
            : topNotice.hide_pages;

        console.log(showPages, hidePages);

        // 用户已关闭通知，不显示
        if (getSession(SESSION_KEY_NOTICE_CLOSED)) {
            setIsVisible(false);
            return;
        }

        // 当前页面类型
        const currentPageType = pageType[page_name];

        // 检查当前页面是否在显示列表中（0表示所有页面）
        if (!showPages.includes(currentPageType) && !showPages.includes(0)) {
            setIsVisible(false);
            return;
        }

        // 检查当前路径是否在隐藏列表中
        const shouldHide = hidePages.some((item: { value: string }) => {
            if (!item.value) return false; // 错误处理
            const hidePathname = item.value.split("?")?.[0];
            return hidePathname === pathname; // 使用严格相等
        });
        setIsVisible(!shouldHide);
    }, [config, topNotice.pages, topNotice.hide_pages, page_name, pathname]);

    // 关闭通知并保存用户选择
    const handleClose = useCallback(() => {
        setIsVisible(false);
        setSession(SESSION_KEY_NOTICE_CLOSED, SESSION_VALUE_NOTICE_CLOSED);
    }, []);

    return {
        isVisible,
        showAfterInitialRender,
        setShowAfterInitialRender,
        checkVisibility,
        handleClose,
    };
};

/**
 * 通知横幅背景样式 hook
 */
const useAnnouncementBackground = (
    topNotice: IAnnouncementProps["topNotice"],
) => {
    return useMemo(() => {
        return {
            background:
                topNotice.backgroundColor ||
                (topNotice.background_image
                    ? `url(${topNotice.background_image}) no-repeat center center`
                    : "#000"),
        };
    }, [topNotice.backgroundColor, topNotice.background_image]);
};

// 为了保持类型兼容性
export interface IChildrenProps extends IAnnouncementProps {}

/**
 * 公告横幅客户端组件
 */
const Client: React.FC<React.PropsWithChildren<IAnnouncementProps>> = (
    props,
) => {
    const { topNotice } = props;
    const announcementRef = useRef<HTMLDivElement>(null);
    const [setHeaderOptions] = headerStore.useHeaderStore((state) => [
        state.setHeaderOptions,
    ]);

    // 使用自定义hooks管理状态和逻辑
    const {
        isVisible,
        showAfterInitialRender,
        setShowAfterInitialRender,
        checkVisibility,
        handleClose,
    } = useAnnouncementVisibility(props);

    const backgroundStyle = useAnnouncementBackground(topNotice);

    // 初始化逻辑
    useEffect(() => {
        // 检查是否应该显示通知
        checkVisibility();

        // 执行可选的滚动回调
        props.onScroll?.();

        // 设置状态以允许轮播图渲染
        setShowAfterInitialRender(true);

        // 更新头部高度信息
        if (announcementRef.current) {
            setHeaderOptions([
                {
                    anouncementHeaderHeight:
                        announcementRef.current.clientHeight,
                },
            ]);
        }
    }, [
        checkVisibility,
        props,
        props.onScroll,
        setHeaderOptions,
        setShowAfterInitialRender,
    ]);

    // 如果不可见，不渲染任何内容
    if (!isVisible) {
        return null;
    }

    return (
        <div
            style={backgroundStyle}
            className={classnames(
                stl["top-notice-container"],
                {
                    [stl["top-notice-container-padding"]]:
                        !topNotice.is_show_swiper,
                },
                props.className,
            )}
            ref={announcementRef}
        >
            {/* 根据配置决定是否使用轮播容器 */}
            {topNotice.is_show_swiper && showAfterInitialRender ? (
                <div className={classnames(stl["carousel-container"])}>
                    {props.children}
                </div>
            ) : (
                props.children
            )}

            {/* 关闭按钮 */}
            <FmIcon
                icon="icon--close"
                className={classnames(
                    stl["close-icon"],
                    stl["top-notice-close"],
                    {
                        ["hidden"]: !topNotice.is_show_close_btn,
                    },
                )}
                onClick={handleClose}
            />
        </div>
    );
};

export default Client;
